import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { useToast } from './Toast';

/**
 * ReductionTypeEditor — the super-admin editor for the global EXEMPTION type list
 * (appeal-lifecycle respec §2.7).
 *
 * ⚠️ NAMING (part 2 §1.1 / §2). The record is an **exemption**; abatement,
 * freeport, homestead and the rest are types under it. The visible copy says
 * exemption. The transport does NOT: the endpoint stays
 * /api/org-settings/reduction-types and the stored key stays `reduction_types`,
 * because that key is where every org's customized list already lives — renaming
 * it would read as an empty list and silently reset each org to the defaults.
 * The component name is kept for the same reason (it matches the endpoint).
 * "Reduction" as a BILLING basis (reduction contingency) is a different concept
 * and is untouched.
 *
 * A row per type: an editable name plus an optional multiselect chip list,
 * "Available for property types". Reads the list from
 * GET /api/org-settings/reduction-types and persists edits via PUT (super_admin
 * only, enforced server-side). Backed by config/reduction-types.js.
 *
 * SCOPE NOTE: the scope chips are STORED but NOT yet acted on (Tier-2 / OQ-gated);
 * `requires_type_id` is part of the shape but deliberately NOT surfaced here (it is
 * a Tier-2 prerequisite-gating field). A round-trip preserves any stored
 * requires_type_id untouched.
 *
 * ⚠️ A second chip list, "Available in states", was REMOVED at the customer's
 * direction (2026-08-05) — it was never acted on and was never asked for. The
 * server strips the field on read and write (config/reduction-types.js
 * `stripStateAvailability`), so this editor never sees it. Do not add it back.
 */

interface ReductionType {
  name: string;
  available_for_property_types?: string[];
  requires_type_id?: string;
}

/** A labelled chip list with add/remove — the "chips-and-add" house pattern. */
function ChipInput({
  label, values, canEdit, onChange, placeholder, ariaPrefix,
}: {
  label: string;
  values: string[];
  canEdit: boolean;
  onChange: (next: string[]) => void;
  placeholder: string;
  ariaPrefix: string;
}) {
  const [text, setText] = useState('');
  function add() {
    const v = text.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { setText(''); return; }
    onChange([...values, v]);
    setText('');
  }
  return (
    <div style={{ marginTop: 'var(--sp-2)' }}>
      <label style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)' }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
        {values.length === 0 && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>Any</span>}
        {values.map((v) => (
          <span key={v} style={{ display: 'inline-flex' }}>
            <Badge variant="info">
              {v}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onChange(values.filter((x) => x !== v))}
                  aria-label={`Remove ${label} ${v}`}
                  style={{ marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent', color: 'var(--color-ink-3)', cursor: 'pointer', fontSize: 'var(--fs-sm)', lineHeight: 1, padding: 0 }}
                >×</button>
              )}
            </Badge>
          </span>
        ))}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
          <Input
            value={text}
            placeholder={placeholder}
            aria-label={`${ariaPrefix} — add`}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          />
          <Button variant="ghost" size="sm" onClick={add} disabled={!text.trim()}>Add</Button>
        </div>
      )}
    </div>
  );
}

export function ReductionTypeEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const listQuery = useQuery<ReductionType[]>({
    queryKey: ['org-settings', 'reduction-types'],
    queryFn: ({ signal }) => api.get<ReductionType[]>('/api/org-settings/reduction-types', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<ReductionType[] | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = listQuery.data;
  const value = draft ?? persisted ?? null;
  const dirty = draft !== null && persisted != null && JSON.stringify(draft) !== JSON.stringify(persisted);

  /** Edit one row by index, normalizing empty arrays to `undefined` so the stored
   *  shape stays clean and the dirty compare is stable. */
  function editRow(idx: number, patch: Partial<ReductionType>) {
    if (!value) return;
    const next = value.map((row, i) => {
      if (i !== idx) return row;
      const merged: ReductionType = { ...row, ...patch };
      if (merged.available_for_property_types && merged.available_for_property_types.length === 0) delete merged.available_for_property_types;
      return merged;
    });
    setDraft(next);
  }

  function addType() {
    if (!value) return;
    setDraft([...value, { name: '' }]);
  }

  function removeType(idx: number) {
    if (!value) return;
    setDraft(value.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!value) return;
    if (value.length === 0) { toast({ variant: 'error', message: 'Keep at least one exemption type.' }); return; }
    if (value.some((r) => !r.name.trim())) { toast({ variant: 'error', message: 'Every exemption type needs a name.' }); return; }
    setSaving(true);
    try {
      await api.put('/api/org-settings/reduction-types', { reduction_types: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'reduction-types'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Exemption types saved.' });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      gridColumn: '1 / -1',
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Exemption types</h3>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          {canEdit && dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={saving}>Reset</Button>
              <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        The global list of exemption types an exemption record can be. Each type may optionally scope to
        states or property types — those are stored now and enforced in the picker later. {canEdit ? '' : 'Editable by a super_admin.'}
      </p>

      {listQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {value.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--color-muted)' }}>No exemption types configured.</p>
            ) : value.map((row, idx) => (
              <div
                key={idx}
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--r-md)',
                  padding: 'var(--sp-3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <Input
                      value={row.name}
                      disabled={!canEdit}
                      placeholder="Type name (e.g. Homestead)"
                      aria-label={`Exemption type ${idx + 1} name`}
                      onChange={(e) => editRow(idx, { name: e.target.value })}
                    />
                  </div>
                  {canEdit && (
                    <Button variant="secondary" size="sm" onClick={() => removeType(idx)} aria-label={`Remove exemption type ${row.name || idx + 1}`}>Remove</Button>
                  )}
                </div>

                <ChipInput
                  label="Available for property types"
                  values={row.available_for_property_types ?? []}
                  canEdit={canEdit}
                  onChange={(next) => editRow(idx, { available_for_property_types: next })}
                  placeholder="Add a property type (e.g. Residential)"
                  ariaPrefix={`${row.name || `Type ${idx + 1}`} property types`}
                />
              </div>
            ))}
          </div>

          {canEdit && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Button variant="secondary" size="sm" onClick={addType}>+ Add type</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
